import React, { useMemo } from 'react'
import { metricThresholds, metricColors, getMetricColor } from '../utils/metricThresholds'

interface GaugeMetricProps {
  metricName: string
  metricValue: number
  minValue?: number
  maxValue?: number
  hideTitle?: boolean
  onColorChange?: (color: string) => void
}

// Metric name mapping
const metricNameMap: { [key: string]: string } = {
  'calculated:f1': 'F1 Score',
  'train:cross_entropy': 'Cross Entropy',
  'train:smooth_l1': 'Smooth L1',
  'validation:mAP50-95': 'mAP',
  'validation:precision': 'Precision',
  'validation:recall': 'Recall',
  'validation:accuracy': 'Validation Accuracy',
  'train:accuracy': 'Train Accuracy',
}

// Function to convert metric keys to human-readable names
export const convertMetricName = (key: string): string => {
  return metricNameMap[key] || key
}

// Function to dynamically generate segments based on thresholds
const getColorSegments = (metricName: string) => {
  const readableMetricName = convertMetricName(metricName)
  const threshold = metricThresholds[readableMetricName]

  if (!threshold) return []

  if (threshold.better === 'higher') {
    // Higher is better (e.g., mAP)
    return [
      { color: metricColors.low, range: threshold.low / 100 }, // Red for bad
      {
        color: metricColors.medium,
        range: (threshold.medium - threshold.low) / 100,
      }, // Yellow for average
      { color: metricColors.high, range: (100 - threshold.medium) / 100 }, // Green for good
    ]
  } else {
    // Lower is better (e.g., Cross Entropy, Smooth L1)
    const maxRange = threshold.low // Use low as the max for 'lower is better' metrics
    return [
      { color: metricColors.high, range: threshold.medium / maxRange }, // Green for good (below medium)
      {
        color: metricColors.medium,
        range: (threshold.low - threshold.medium) / maxRange,
      }, // Yellow for average
      { color: metricColors.low, range: 1 }, // Red for bad (anything higher than low)
    ]
  }
}

const GaugeMetric: React.FC<GaugeMetricProps> = ({
  metricName,
  metricValue,
  minValue = 0,
  maxValue = 100,
  onColorChange,
}) => {
  const percentValue = (metricValue - minValue) / (maxValue - minValue)
  const readableMetricName = convertMetricName(metricName)

  // Get the dynamic color segments based on the metric type
  const segments = getColorSegments(metricName)

  // Get the text color based on the metric value
  const textColor = useMemo(() => {
    return getMetricColor(readableMetricName, metricValue)
  }, [readableMetricName, metricValue])

  // Calculate current color based on needle position
  React.useEffect(() => {
    if (!onColorChange || segments.length === 0) return

    let currentSegment = segments[0]
    let accumulatedRange = 0

    for (const segment of segments) {
      accumulatedRange += segment.range
      if (percentValue <= accumulatedRange) {
        currentSegment = segment
        break
      }
    }

    onColorChange(currentSegment.color)
  }, [percentValue, segments, onColorChange])

  // Optimized size values for more compact gauges
  const baseWidth = 200
  const baseHeight = 150
  const baseRadius = 65
  const baseNeedleLength = 55
  const baseCenterX = 100
  const baseCenterY = 95
  const baseCenterCircleRadius = 3

  // Calculate tick marks
  const createTicks = () => {
    const ticks = []
    for (let i = 0; i <= 10; i++) {
      const angle = i / 10
      const isMainTick = i % 2 === 0
      const tickLength = isMainTick ? 8 : 5
      const startRadius = baseRadius + 2
      const endRadius = startRadius + tickLength

      const startX = baseCenterX + startRadius * Math.cos(Math.PI * (1 - angle))
      const startY = baseCenterY - startRadius * Math.sin(Math.PI * (1 - angle))
      const endX = baseCenterX + endRadius * Math.cos(Math.PI * (1 - angle))
      const endY = baseCenterY - endRadius * Math.sin(Math.PI * (1 - angle))

      ticks.push(
        <line
          key={`tick-${i}`}
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke="#e2e8f0"
          strokeWidth={isMainTick ? 1.5 : 1}
          opacity={0.8}
          vectorEffect="non-scaling-stroke"
        />
      )

      // Add labels for main ticks
      if (isMainTick) {
        const labelRadius = endRadius + 12
        const labelX = baseCenterX + labelRadius * Math.cos(Math.PI * (1 - angle))
        const labelY = baseCenterY - labelRadius * Math.sin(Math.PI * (1 - angle))
        ticks.push(
          <text
            key={`label-${i}`}
            x={labelX}
            y={labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#e2e8f0"
            fontSize="12"
            fontWeight="500"
          >
            {i * 10}
          </text>
        )
      }
    }
    return ticks
  }

  const getSegmentPath = (startAngle: number, endAngle: number) => {
    const startX = baseCenterX + baseRadius * Math.cos(Math.PI * (1 - startAngle))
    const startY = baseCenterY - baseRadius * Math.sin(Math.PI * (1 - startAngle))
    const endX = baseCenterX + baseRadius * Math.cos(Math.PI * (1 - endAngle))
    const endY = baseCenterY - baseRadius * Math.sin(Math.PI * (1 - endAngle))
    return `M ${startX} ${startY} A ${baseRadius} ${baseRadius} 0 0 1 ${endX} ${endY}`
  }

  let prevAngle = 0

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${baseWidth} ${baseHeight}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {/* Subtle gradient for the background arc */}
        <linearGradient id="backgroundGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1a1a1a" />
          <stop offset="100%" stopColor="#2d3748" />
        </linearGradient>

        {/* Subtle glow for the colored segments */}
        <filter id="segmentGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Metallic gradient for the needle */}
        <linearGradient id="needleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#e2e8f0" />
          <stop offset="50%" stopColor="#f7fafc" />
          <stop offset="100%" stopColor="#e2e8f0" />
        </linearGradient>

        {/* Center circle gradient */}
        <radialGradient id="centerGradient">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e2e8f0" />
        </radialGradient>
      </defs>

      {/* Background arc with gradient */}
      <path
        d={getSegmentPath(0, 1)}
        fill="none"
        stroke="url(#backgroundGradient)"
        strokeWidth={6}
        vectorEffect="non-scaling-stroke"
      />

      {/* Tick marks */}
      {createTicks()}

      {/* Colored segments with subtle glow */}
      {segments.map((segment, idx) => {
        const startAngle = prevAngle
        const endAngle = prevAngle + segment.range
        prevAngle = endAngle
        return (
          <path
            key={idx}
            d={getSegmentPath(startAngle, endAngle)}
            fill="none"
            stroke={segment.color}
            strokeWidth={6}
            strokeLinecap="round"
            opacity={0.9}
            vectorEffect="non-scaling-stroke"
            filter="url(#segmentGlow)"
          />
        )
      })}

      {/* Needle shadow */}
      <line
        x1={baseCenterX}
        y1={baseCenterY}
        x2={baseCenterX + baseNeedleLength * Math.cos(Math.PI * (1 - percentValue))}
        y2={baseCenterY - baseNeedleLength * Math.sin(Math.PI * (1 - percentValue))}
        stroke="rgba(0,0,0,0.2)"
        strokeWidth={3}
        strokeLinecap="round"
        transform="translate(1.5,1.5)"
        vectorEffect="non-scaling-stroke"
      />

      {/* Needle with metallic gradient */}
      <line
        x1={baseCenterX}
        y1={baseCenterY}
        x2={baseCenterX + baseNeedleLength * Math.cos(Math.PI * (1 - percentValue))}
        y2={baseCenterY - baseNeedleLength * Math.sin(Math.PI * (1 - percentValue))}
        stroke="url(#needleGradient)"
        strokeWidth={2}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Center circle shadow */}
      <circle
        cx={baseCenterX + 1}
        cy={baseCenterY + 1}
        r={baseCenterCircleRadius + 1}
        fill="rgba(0,0,0,0.2)"
        vectorEffect="non-scaling-stroke"
      />

      {/* Center circle with gradient */}
      <circle
        cx={baseCenterX}
        cy={baseCenterY}
        r={baseCenterCircleRadius}
        fill="url(#centerGradient)"
        vectorEffect="non-scaling-stroke"
      />

      {/* Percentage value with dynamic color */}
      <text
        x={baseCenterX}
        y={baseCenterY + 25}
        textAnchor="middle"
        fill={textColor}
        fontSize="13"
        fontWeight="600"
        style={{ textShadow: '0px 0px 2px rgba(0,0,0,0.7)' }}
      >
        {(metricValue ?? 0).toFixed(1)}%
      </text>
    </svg>
  )
}

export default GaugeMetric
